import Head from 'next/head';
import localeCode from 'locale-code';
import {
    isOkToLoad
} from '../../utils/isOkToLoad';
import {
    FACEBOOK_PIXEL_ID,
    MetaTrackingEventName,
    MetaTrackingTypeEnum
} from '../../lib/tracking/meta';
import {
    SNAP_PIXEL_ID
} from '../../lib/tracking/snapchat';
import { SnapEventName } from '../../lib/tracking/snapchat';
import { COOKIE_KEYS, normalizeCountry, normalizeEmail, normalizeName, normalizePhone, sha256Hash } from '../../lib/tracking/utils';
import { useContext, useMemo } from 'react';
import SessionContext from '../../contexts/session';
import { useCookies } from 'react-cookie';
import { TIKTOK_DIGITAL_PIXEL_ID, TIKTOK_MED_PIXEL_ID } from '../../lib/tracking/tiktok';
import { REDDIT_PIXEL_ID, RedditClientSideEvent } from '../../lib/tracking/reddit';
import { X_PIXEL_ID, XEvent } from '../../lib/tracking/x';
import { NOT_LOADED_HINT } from '../../pages/_app';

const MedTrackingScripts = () => {
    const { user } = useContext(SessionContext);
    const geo_country = useMemo(() => {
        const locale = Intl.DateTimeFormat().resolvedOptions().locale;
        return localeCode.getCountryCode(locale);
    }, []);

    const [cookies] = useCookies();

    const snapInitParamsString = useMemo(() => {
        const params = {
            ...(user?.email && user?.email !== NOT_LOADED_HINT ? {user_hashed_email: sha256Hash(normalizeEmail(user.email))} : {}),
            ...(user?.phone ? {user_hashed_phone_number: sha256Hash(normalizePhone(user.phone))} : {}),
            ...(user?.givenName ? {firstname: sha256Hash(normalizeName(user.givenName))} : {}),
            ...(user?.familyName ? {lastname: sha256Hash(normalizeName(user.familyName))} : {}),
            ...(geo_country ? {geo_country: sha256Hash(normalizeCountry(geo_country))} : {}),
            ...(cookies[COOKIE_KEYS.SNAP_CLICK_ID] ? {click_id: cookies[COOKIE_KEYS.SNAP_CLICK_ID]} : {}),
            ...(cookies[COOKIE_KEYS.SNAP_COOKIE1] ? {uuid_c1: cookies[COOKIE_KEYS.SNAP_COOKIE1]} : {})
        };

        return Object.entries(params).map(([key, value]) => `${key}: '${value}'`).join(', ');
    }, [user, geo_country]);

    const redditInitParamsString = useMemo(() => {
        const params = {
            ...(user?.email && user?.email !== NOT_LOADED_HINT ? { email: sha256Hash(normalizeEmail(user.email))} : {}),
            ...(user?.phone ? { phoneNumber: sha256Hash(normalizePhone(user.phone))} : {}),
            ...(user?.id ? { externalId: sha256Hash(user?.id) } : {})
        };
        return Object.entries(params).map(([key, value]) => `${key}: '${value}'`).join(', ');
    }, [user]);

    const xInitParamsString = useMemo(() => {
        const params = {
            ...(user?.email && user?.email !== NOT_LOADED_HINT ? { email_address: sha256Hash(normalizeEmail(user.email))} : {}),
            ...(user?.phone ? { phone_number: sha256Hash(normalizePhone(user.phone))} : {}),
            ...(cookies[COOKIE_KEYS.X_CLICK_ID] ? { twclid: cookies[COOKIE_KEYS.X_CLICK_ID] } : {}),
        };
        return Object.entries(params).map(([key, value]) => `${key}: '${value}'`).join(', ');
    }, [user]);

    return (
        <Head>
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', '${FACEBOOK_PIXEL_ID.MEDS}');
                            fbq('${MetaTrackingTypeEnum.TRACK}', '${MetaTrackingEventName.PAGE_VIEW}');
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <noscript>
                    <img
                        height="1"
                        width="1"
                        style={{
                            display: 'none'
                        }}
                        src={`https://www.facebook.com/tr?id=${FACEBOOK_PIXEL_ID.MEDS}&ev=PageView&noscript=1`}
                    />
                </noscript>
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html:
                        `
                            !function (w, d, t) {
                            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
                            var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
                            ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
                            ttq.load('${TIKTOK_DIGITAL_PIXEL_ID}');
                            ttq.page();
                            }(window, document, 'ttq');
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html:
                        `
                            !function (w, d, t) {
                            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
                            var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
                            ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
                            ttq.load('${TIKTOK_MED_PIXEL_ID}');
                            ttq.page();
                            }(window, document, 'ttq');
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                            (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
                            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
                            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
                            r.src=n;var u=t.getElementsByTagName(s)[0];
                            u.parentNode.insertBefore(r,u);})(window,document,
                            'https://sc-static.net/scevent.min.js');
                            snaptr('init', '${SNAP_PIXEL_ID}', {${snapInitParamsString}});
                            snaptr('track', '${SnapEventName.PAGE_VIEW}');
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function(w,d){if(!w.rdt){var p=w.rdt=function()
                            {p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};
                            p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js?pixel_id=${REDDIT_PIXEL_ID}",t.async=!0;
                            var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
                            rdt('init','${REDDIT_PIXEL_ID}');rdt('track', '${RedditClientSideEvent.PAGE_VISIT}', {${redditInitParamsString}});
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
                            },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
                            a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
                            twq('config','${X_PIXEL_ID}');
                            twq('event', '${XEvent.PageView}', {${xInitParamsString}});
                        `
                    }}
                />
            )}
        </Head>
    );
};

export default MedTrackingScripts;
